import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';

/**
 * LEAVING THE ITEM FORM IN THE LIST'S DETAIL PANE (KPR-1644; owner ruling i10).
 *
 * The pane edits an item without leaving the list, so everything that moves
 * the list away from the item being edited is a way out of the form: ↑/↓ and
 * the pane's ‹ ›, a row click, closing the pane (its ✕ or Esc), and a change
 * of card, filter or sort. Every one of them changes the address (`?item=`,
 * `?card=`, `?q=`, `?sort=`… — hooks/useItemsViewParams), so the one router
 * blocker (contexts/UnsavedChangesContext) holds them all; `paneFormLeaves`
 * says which address changes count. A change of view, grouping, columns or
 * the pane's width keeps the form on screen and is not leaving.
 *
 * With unsaved edits the person is asked **Save · Discard · Stay**, rather
 * than the platform's Discard / Keep editing, because the pane's next move
 * is usually "save this one and go to the next":
 *
 *   Save     the form's own save (every rule it applies); the move goes on
 *            only once the save lands. A save stopped by the form (a field to
 *            fix) or refused by keepr stays, with the reason in the pane.
 *   Discard  what the form held goes (its uploads too) and the pane is back
 *            on the read view, then the move — a change of sort or filter
 *            keeps the same item selected, and it must not keep the form
 *            showing what was thrown away. Off while a save is in flight.
 *   Stay     nothing happens. Esc, the ✕ and the mask are Stay too, and Stay
 *            has the focus, so Enter never drops or writes anything.
 *
 * Cancel asks the same question when there is something to lose, and goes
 * back to the read view at once when there is not.
 *
 *   on             the pane variant of the form (false: everything is inert)
 *   saving         the form's write in flight (null when none)
 *   submittingRef  true from the moment a save is sent until it settles
 *   save()         the form's Save (handleSubmit)
 *   discard()      drop the form's edits (settle uploads, mark pristine)
 *   close()        back to the read view
 *   onSaved(item)  a save landed: the pane shows the saved item
 *
 * Returns { onBlocked, cancel(dirty), saved(data), prompt }: `onBlocked` is
 * useDirtyForm's, `saved` replaces the page form's navigate after a save,
 * `prompt` is the dialog to render.
 */

// The list parameters whose change takes the list away from the item being
// edited (utils/itemsView ITEMS_VIEW_PARAMS): the selection, and the card,
// filter and sort that decide what the list shows.
export const PANE_LEAVE_PARAMS = Object.freeze([
	'item', 'card', 'q', 'review', 'saved', 'linked',
	'sort', 'dir', 'sort2', 'dir2', 'sort3', 'dir3',
]);

export function paneFormLeaves({ currentLocation, nextLocation }) {
	if (!currentLocation || !nextLocation) return true;
	if (currentLocation.pathname !== nextLocation.pathname) return true;
	const before = new URLSearchParams(currentLocation.search || '');
	const after = new URLSearchParams(nextLocation.search || '');
	return PANE_LEAVE_PARAMS.some(name => before.get(name) !== after.get(name));
}

export const PANE_LEAVE_TITLE = 'Save your changes to this item?';

export default function usePaneFormLeave({ on, saving, submittingRef, save, discard, close, onSaved }) {
	// The open question: { proceed, reset } for a held move, { cancel: true }
	// for Cancel, or null.
	const [ask, setAsk] = useState(null);
	const askRef = useRef(null);
	askRef.current = ask;
	// The move (or the Cancel) waiting on a save the question started.
	const pendingRef = useRef(null);
	const latest = useRef({});
	latest.current = { save, discard, close, onSaved };

	const onBlocked = useCallback(({ proceed, reset }) => {
		if (!on) return false;
		setAsk({ proceed, reset });
		return true;
	}, [on]);

	const cancel = useCallback((dirty) => {
		if (!dirty) {
			latest.current.close();
			return;
		}
		setAsk({ cancel: true });
	}, []);

	const answer = useCallback((choice) => {
		const asked = ask;
		setAsk(null);
		if (!asked) return;
		if (choice === 'stay') {
			asked.reset?.();
			return;
		}
		if (choice === 'discard') {
			latest.current.discard();
			latest.current.close();
			asked.proceed?.();
			return;
		}
		// Save: one already in flight counts as this one.
		pendingRef.current = asked;
		if (!submittingRef.current) latest.current.save();
		if (!submittingRef.current) {
			// The form stopped it before sending (a field to fix): stay.
			pendingRef.current = null;
			asked.reset?.();
		}
	}, [ask, submittingRef]);

	// A save that settled without landing (refused, failed, or a conflict to
	// settle first): the move it was for does not happen.
	useEffect(() => {
		if (saving || submittingRef.current || !pendingRef.current) return;
		const pending = pendingRef.current;
		pendingRef.current = null;
		pending.reset?.();
	}, [saving, submittingRef]);

	// A save landed: the pane shows the saved item, then the move goes on. A
	// save made while the question was still open (⌘↵ in the form) answers it
	// as Save would have.
	const saved = useCallback((data) => {
		const pending = pendingRef.current || askRef.current;
		pendingRef.current = null;
		if (askRef.current) setAsk(null);
		latest.current.onSaved?.(data);
		pending?.proceed?.();
	}, []);

	const prompt = on ? (
		<Modal
			open={Boolean(ask)}
			title={PANE_LEAVE_TITLE}
			onCancel={() => answer('stay')}
			footer={[
				<Button key="stay" autoFocus onClick={() => answer('stay')}>Stay</Button>,
				<Button key="discard" danger disabled={Boolean(saving)} onClick={() => answer('discard')}>Discard</Button>,
				<Button key="save" type="primary" onClick={() => answer('save')}>Save</Button>,
			]}
		>
			<p className="item-pane__leave-prompt">You changed this item and have not saved it.</p>
		</Modal>
	) : null;

	return { onBlocked, cancel, saved, prompt };
}
